import React from 'react';
import { Seo } from '../../components/Seo';
import { graphql } from 'gatsby';
import Layout from '../../components/Layout';
import styled from 'styled-components';
import Heading from '../../components/Heading';
import WebinarHero from '../../components/WebinarHero';
import WebinarOverview from '../../components/WebinarOverview';
import ImageRow from '../../components/ImageRow';
import TheBreakdown from '../../components/TheBreakdown';
import sortByNewest from '../../hooks/sortByNewest';
import { getImage } from 'gatsby-plugin-image';
import OpenGraph from '../../images/open-graph.png';

const StyledWebinar = styled.div`
  margin-top: 1px;
  border-bottom: 1px solid var(--primary);
  padding-bottom: 5rem;
`;

const Webinar = ({ data }) => {
  const webinar = data?.prismicWebinar.data;
  const topics = webinar.topics.text.split(',');

  const speakers = [
    webinar?.speaker_1?.document?.data,
    webinar?.speaker_2?.document?.data,
    webinar?.speaker_3?.document?.data,
  ];

  const periodFormatDate = (date) => {
    const formattedDate = new Date(date);

    return formattedDate
      .toLocaleDateString('en-us', {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
      })
      .replace(/\//g, '.');
  };

  const timeFormatDate = (date) => {
    const formattedDate = new Date(date);

    return formattedDate
      .toLocaleTimeString('en-us', {
        hour: 'numeric',
        hour12: true,
        timeZone: 'America/Chicago',
        timeZoneName: 'short',
      })
      .replace(/,/g, '');
  };

  return (
    <Layout CTA>
      <StyledWebinar>
        <WebinarHero
          headingText={webinar.title}
          date={periodFormatDate(data.prismicWebinar.data.date_time)}
          time={timeFormatDate(data.prismicWebinar.data.date_time)}
          img={webinar.header_image.url}
          video={webinar.webinar_link?.url}
          register={webinar.register_link?.url}
          unformattedDate={data.prismicWebinar.data.date_time}
        />

        <WebinarOverview whatToExpect={webinar.what_to_expect.richText} topics={topics} />
        <Heading withLine headingLevel={3} headingText="Meet the Speakers" widthWrapper />
        <ImageRow
          images={speakers.map((speaker) => ({
            image: getImage(speaker?.image?.gatsbyImageData),
            alt: speaker?.name,
            title: speaker?.name,
            description: speaker?.title,
            type: 'team',
          }))}
        />
        <TheBreakdown
          images={data?.allPrismicWebinar.nodes.slice(0, 3).map((webinar) => ({
            image: getImage(webinar.data.header_image.gatsbyImageData),
            alt: 'Webinar header image',
            title: webinar.data.title,
            date: webinar.data.date_time,
            type: 'webinar',
          }))}
        />
        <Heading withLine headingLevel={3} headingText="Related Content" widthWrapper />
        <ImageRow
          images={sortByNewest(
            data.allPrismicWebinar.nodes.map((item) => ({
              image: getImage(item.data.header_image?.gatsbyImageData)
                ? getImage(item.data.header_image.gatsbyImageData)
                : 'https://images.prismic.io/bcvsocial/04247e7c-56ac-443d-91d8-113cb1e91444_191008_JW-Marriott-DC_GW_Pre-Selects-25-1-e1575570515666.jpg?ixlib=gatsbyFP&auto=compress%2Cformat&fit=max',
              alt: item.data.header_image?.alt
                ? item.header_image?.alt
                : 'Related content header image',
              title: item.data.title,
              type: 'webinar',
              date: item.data.date_posted ? item.data.date_posted : item.first_publication_date,
            }))
          ).slice(0, 3)}
        />
      </StyledWebinar>
    </Layout>
  );
};

export const WebinarQuery = graphql`
  query ($id: String!) {
    prismicWebinar(id: { eq: $id }) {
      data {
        date_time
        header_image {
          alt
          url
        }
        webinar_link {
          url
        }
        register_link {
          url
        }
        speaker_1 {
          document {
            ... on PrismicTeamMember {
              data {
                image {
                  gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
                }
                name
                title
              }
            }
          }
        }
        speaker_2 {
          document {
            ... on PrismicTeamMember {
              data {
                image {
                  gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
                }
                title
                name
              }
            }
          }
        }
        speaker_3 {
          document {
            ... on PrismicTeamMember {
              data {
                name
                title
                image {
                  gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
                }
              }
            }
          }
        }
        title
        topics {
          text
        }
        webinar_link {
          url
        }
        what_to_expect {
          richText
        }
      }
    }
    allPrismicWebinar {
      nodes {
        first_publication_date
        data {
          date_posted
          header_image {
            alt
            gatsbyImageData(placeholder: BLURRED, width: 800, height: 800)
          }
          topics {
            text
          }
          title
          webinar_link {
            url
          }
          register_link {
            url
          }
          date_time(formatString: "MMMM DD, YYYY")
        }
      }
    }
  }
`;

export const Head = ({ data }) => (
  <Seo title={data.prismicWebinar.data.title}>
    <meta property="og:title" content={data.prismicWebinar.data.title} />
    <meta property="og:image" content={OpenGraph} />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:type" content="website" />
  </Seo>
);

export default Webinar;
